/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * Turns a raw audit diff into the shape every renderer consumes.
 *
 * Renderers receive normalized changes and are responsible only for presentation. That is what
 * keeps adding an entity type to one file: the parsing, the operation-code tolerance and the
 * defensive handling all happen once, here.
 *
 * Everything is defensive. The diff comes from an upstream store, is not guaranteed to be an array,
 * and its entries are not guaranteed to be well formed. A malformed record must still render.
 */

import { labelForPath } from './fieldLabels'

import type { AuditDiffEntry } from '../../types'

export type ChangeState = 'ADDED' | 'MODIFIED' | 'REMOVED'

/** goldeye writes the numeric enum; older records carry the string names. */
const ADD = new Set<number | string>([0, '0', 'Add', 'add'])
const REPLACE = new Set<number | string>([1, '1', 'Replace', 'replace'])
const REMOVE = new Set<number | string>([2, '2', 'Remove', 'remove'])

export interface NormalizedChange {
  /** Stable within one record; used as a React key. */
  key: string
  state: ChangeState
  /** Human label, never a raw path. */
  label: string
  /** The raw dot path, for the support escape hatch. */
  path: string
  /** Path segments, so a renderer can group by e.g. zone or severity without re-parsing. */
  segments: string[]
  /** Present for MODIFIED and REMOVED. */
  before?: unknown
  /** Present for MODIFIED and ADDED. */
  after?: unknown
}

export const stateOf = (o: number | string): ChangeState => {
  if (ADD.has(o)) return 'ADDED'
  if (REMOVE.has(o)) return 'REMOVED'
  // An unrecognised operation is treated as a modification: it is the only state that can show
  // both sides, so nothing in the record is hidden by our not understanding the code.
  return 'MODIFIED'
}

/** `diff` is not guaranteed to be an array in historical records. */
export const toDiffArray = (diff: unknown): AuditDiffEntry[] =>
  Array.isArray(diff)
    ? diff.filter((entry): entry is AuditDiffEntry => !!entry && typeof entry === 'object' && 'p' in entry)
    : []

export const normalizeDiff = (diff: unknown): NormalizedChange[] =>
  toDiffArray(diff).map((entry, index) => {
    const path = typeof entry.p === 'string' ? entry.p : ''
    const state = stateOf(entry.o)

    return {
      key: `${path}-${index}`,
      state,
      label: labelForPath(path),
      path,
      segments: path ? path.split('.') : [],
      // For a replace, `v` is the value *before* the change and `r` the replacement.
      before: state === 'ADDED' ? undefined : entry.v,
      after: state === 'REMOVED' ? undefined : REPLACE.has(entry.o) ? entry.r : entry.v,
    }
  })

/**
 * Group changes by a caller-supplied key, preserving first-seen order.
 *
 * Insertion order matters: the diff arrives in the order the producer walked the entity, which is
 * usually the order a person would expect to read it.
 */
export const groupBy = <T>(items: T[], keyOf: (item: T) => string): Map<string, T[]> => {
  const groups = new Map<string, T[]>()

  for (const item of items) {
    const key = keyOf(item)
    const existing = groups.get(key)
    if (existing) existing.push(item)
    else groups.set(key, [item])
  }

  return groups
}

/** Split into the three buckets the generic renderer presents. */
export const byState = (changes: NormalizedChange[]) => ({
  ADDED: changes.filter((c) => c.state === 'ADDED'),
  MODIFIED: changes.filter((c) => c.state === 'MODIFIED'),
  REMOVED: changes.filter((c) => c.state === 'REMOVED'),
})
